import {
    Ecommerce as ListingsEcommerce,
    Taxonomy as OnlyListingsTaxonomy
} from './listings';

import { Taxonomy as OnlyListingTaxonomy } from './listing';
import { Ecommerce as LeadEcommerce } from './lead';
import { Taxonomy as OnlyContactTaxonomy } from './contact';
import {
    Taxonomy as OnlyPropertyTaxonomy,
    Ecommerce as PropertyEcommerce,
    PropertyResultsEcommerce
} from './property';

import {
    Taxonomy as OnlyAgentsTaxonomy,
    AgentsEcommerce,
    AgentEcommerce
} from './agents';
import { EmailAllResultEcommerce } from '../../graphql.generated';
import { AreaGuidesEcommerce } from './content';

export type TrackingContract = {
    baselayer: Baselayer;
    taxonomy?: Taxonomy | Record<string, unknown>;
    ecommerce?: Ecommerce | Record<string, unknown>;
};

export type TaxonomyPage =
    | '/homepage/'
    | '/home-values/'
    | '/valuation/'
    | '/privacy/'
    | '/discover/'
    | '/property/details/'
    | '/for-sale/results/'
    | '/for-sale/details/'
    | '/for-sale/contact/'
    | '/to-rent/results/'
    | '/to-rent/details/'
    | '/to-rent/contact/'
    | '/new-homes/results/'
    | '/new-homes/details/'
    | '/new-homes/contact/'
    | '/signin/'
    | '/register/'
    | '/schemes/help-to-buy/'
    | '/schemes/help-to-buy/scotland/'
    | '/schemes/help-to-buy/wales/'
    | '/schemes/shared-ownership/'
    | '/house-prices/'
    | '/house-prices/results/'
    | '/my-home/select/'
    | '/myaccount/my-home/'
    | '/myaccount/my-home/local-activity/'
    | '/myaccount/my-home/estimate/'
    | '/myaccount/my-home/equity/'
    | '/myaccount/my-home/local-market-stats/'
    | '/myaccount/my-home/seller-insights/'
    | '/myaccount/my-home/seller-insights/time-to-sell'
    | '/myaccount/my-situation/'
    | '/myaccount/account/password/'
    | '/property/uprn/'
    | '/find-agents/'
    | '/find-agents/estate-agents/results/'
    | '/find-agents/letting-agents/results/'
    | '/find-agents/commercial-agents/results/'
    | '/agent-matcher/'
    | '/guides/buying-a-home/'
    | '/guides/selling-a-home/'
    | '/personal-estimate/'
    | '/personal-estimate/result/'
    | '/contact/'
    | '/guides/areas/';

export type TaxonomySection =
    | 'homepage'
    | 'home-values'
    | 'house-prices'
    | 'for-sale'
    | 'to-rent'
    | 'new-homes'
    | 'privacy'
    | 'discover'
    | 'guides'
    | 'chapter'
    | 'signin'
    | 'register'
    | 'help-to-buy'
    | 'shared-ownership'
    | 'new homes/developers'
    | 'my-home'
    | 'myaccount'
    | 'find-agents'
    | 'agent-matcher'
    | 'listing-contact';

export type BaseTaxonomy = {
    section: TaxonomySection;
    page: TaxonomyPage;
    pagePosition?: 'top';
    countryCode?:
        | 'gb'
        | 'es'
        | 'fr'
        | 'us'
        | 'pt'
        | 'it'
        | 'ie'
        | 'gr'
        | 'de'
        | 'za'
        | 'ch'
        | 'th'
        | 'tr'
        | 'cy'
        | 'ae';
    signedInStatus?: 'signed in' | 'signed out';
    brand: string;
    brandPrimary?: string;
    os?: string;
    platform?: 'desktop' | 'responsive' | 'mobile' | 'React';
    path?: string;
    listingsCategory?: 'residential' | 'development' | '';
    activity?:
        | 'house_prices_search'
        | 'listing_search'
        | 'listing_details'
        | 'listing_contact'
        | 'editorial'
        | 'development details page'
        | 'agent_developer_directory'
        | 'email_lead_conversion'
        | 'my_home_landing'
        | 'my_home_hub'
        | 'property_details'
        | 'my_home_estimate'
        | 'my_home_notifications'
        | 'my_home_seller_insights'
        | 'personal_estimate'
        | 'agent_matcher'
        | 'my_home_equity'
        | 'area_guide';
    aboutMeStatus?: string;
    device?: string;
    editorialFormat?: string;
    editorialTopic?: string[];
    sharePlatform?: 'facebook' | 'twitter' | 'copy link' | 'whatsapp';
    userId?: string;
    branch_id?: number;
};

export type Baselayer = {
    session_id?: string;
    instance_id?: string;
    device_id?: string;
    session_count?: number;
    event_id?: string;
};

export type Ecommerce =
    | ListingsEcommerce
    | LeadEcommerce
    | PropertyEcommerce
    | AgentsEcommerce
    | AgentEcommerce
    | EmailAllResultEcommerce
    | PropertyResultsEcommerce
    | AreaGuidesEcommerce;

export type {
    AgentEcommerce,
    AgentsEcommerce,
    Agent as AgentData
} from './agents';

export type { AreaGuidesEcommerce } from './content';

export type Taxonomy =
    | BaseTaxonomy
    | ListingsTaxonomy
    | ListingTaxonomy
    | ContactTaxonomy
    | PropertyTaxonomy
    | AgentsTaxonomy;
export type ListingsTaxonomy = BaseTaxonomy & OnlyListingsTaxonomy;
export type ListingTaxonomy = BaseTaxonomy & OnlyListingTaxonomy;
export type ContactTaxonomy = ListingTaxonomy & OnlyContactTaxonomy;
export type PropertyTaxonomy = BaseTaxonomy &
    OnlyPropertyTaxonomy &
    Record<string, unknown>;
export type AgentsTaxonomy = BaseTaxonomy & OnlyAgentsTaxonomy;

export const EventNames = {
    SEARCH: 'Search',
    SEARCH_RESULT_IMPRESSION: 'Search Result Impression',
    VIEWED_PAGE: 'Viewed Page',
    VIEWED_VIRTUAL_PAGE: 'Viewed Virtual Page',
    WIDGET_IMPRESSION: 'Widget Impression',
    WIDGET_INTERACTION: 'Widget Interaction',
    WIDGET_CLICKOUT: 'Widget Clickout',
    LEAD_SENT: 'Lead Sent',
    CLICK_NAV_BAR: 'Click Nav Bar',
    CLICK_SEARCH_RESULT: 'Search Result Click',
    CLICK_SAVE_SEARCH: 'Click Save Search',
    CLICK_SAVE_SEARCH_SUBMITTED: 'Click Save Search Submitted',
    CLICK_EMAIL_PROPERTY_ALERT: 'Click Email Property Alert',
    CLICK_EMAIL_PROPERTY_ALERT_SUBMITTED:
        'Click Email Property Alert Submitted',
    CLICK_CALL_AGENT: 'Click Call Agent',
    CLICK_EMAIL_AGENT: 'Click Email Agent',
    CLICK_SAVE_LISTING: 'Click Save Listing',
    CLICK_BOOK_AGENT_VALUATION: 'Click Book Agent Valuation',
    DEVELOPMENT_LEAD: 'Development Lead',
    SHARE_CONTENT: 'Share Content',
    EMAIL_OPT_IN: 'Email Marketing Opt-In',
    REGISTRATION: 'Registration',
    SIGN_IN: 'Sign In',
    LISTING_LEAD_SENT: 'Listing Lead Sent',
    PHONE_CLICK_TO_REVEAL: 'Phone Click To Reveal',
    APPLICANT_LEAD_SUBMIT: 'Applicant Lead Submit',
    CLICK_EMAIL_ALERT: 'Click Email Alert',
    TRACK_PROPERTY: 'Track Property Success',
    UNTRACK_PROPERTY: 'Untrack Property',
    CLICK_EDIT_SEARCH: 'Click Edit Search',
    CLICK_ENABLE_EMAIL_ALERT: 'Click Enable Email Alert',
    CLAIM_HOME: 'Claim My Home Success',
    MODULE_SHOWN: 'Module Shown',
    MODULE_INTERACTION: 'Module Interaction',
    CLICK_MY_HOME_BOOK_AGENT_VALUATION: 'Click Valuation CTA',
    CLICK_MY_HOME_STATIC_MAP: 'Click Static Map',
    CLICK_MY_HOME_LOCAL_MARKET_STATS: 'Click Local Market Stats CTA',
    ALERTS_CONSENT_ON: 'Alerts Consent On',
    ALERTS_CONSENT_OFF: 'Alerts Consent Off',
    MODAL_SHOWN: 'Modal Shown',
    MODAL_DISMISSED: 'Modal Dismissed',
    PROMO_BANNER_SHOWN: 'Promo Banner Shown',
    PROMO_BANNER_DISMISSED: 'Promo Banner Dismissed',
    CLICKTHROUGH_PROMO_BANNER: 'Clickthrough Promo Banner',
    CLICK_PROPERTY_TAB: 'Click Property Tab',
    CLICK_RENTAL_TAB: 'Click Rental Tab',
    CLICK_ESTIMATE_INFO: 'Click Estimate Info',
    CLICK_VALUATION_CTA: 'Click Valuation CTA',
    CLICK_ESTIMATE_GRAPH: 'Click Estimate Graph',
    CLICK_NOTIFICATION: 'Notification Click',
    DROPDOWN_SELECT: 'Dropdown Select',
    AGENT_LEAD_SUBMIT: 'Agent Lead Submit',
    SORT_APPLIED: 'Sort Applied',
    FORM_STEP: 'Form Step',
    VENDOR_LEAD_SENT: 'Vendor Lead Sent',
    CAROUSEL_IMPRESSION: 'Carousel Impression',
    CAROUSEL_CLICK: 'Carousel Click',
    OUTBOUND_CLICK: 'Outbound Click',
    NOTIFICATION_LOADED: 'Notification loaded',
    CLICK_HISTORIC_LISTING: 'Click historic listing',
    CLICK_LIVE_LISTING: 'Click live listing',
    CLICK_TIMELINE_GALLERY_THUMBNAIL: 'Click timeline gallery thumbnail',
    PERSONAL_ESTIMATE_START: 'Personal Estimate Start',
    PERSONAL_ESTIMATE_SENT: 'Personal Estimate Sent',
    PERSONAL_ESTIMATE_IMPRESSION: 'Personal Estimate Impression',
    PERSONAL_ESTIMATE_PROPERTY_TYPE: 'Personal Estimate Property Type',
    PERSONAL_ESTIMATE_BEDROOMS: 'Personal Estimate Bedrooms',
    FIND_AGENTS_VALUATION: 'Find Agent Valuation',
    TRACKING_MORTGAGE_EVENT: 'Click Mortgage Enquiry',
    TRACKING_STAMPDUTY_EVENT: 'Stamp Duty Calculate',
    SEARCH_CLICK: 'Search Click',
    MOJO_OUTBOUND_CLICK: 'Mojo Outbound Click',
    VALUATION_INTERCEPT_ACCEPT: 'Valuation Intercept Accept',
    VALUATION_INTERCEPT_DECLINE: 'Valuation Intercept Decline'
} as const;

type Values<T> = T[keyof T];

/*
 *  The CMS can define basic event tracking which, as it's user-generated, falls
 *  outside the list of EventNames above. This type is named so the `Cms`
 *  prefix should make it helpful enough to understand where possible values
 *  can come from.
 *
 *  We implemented this Type to remove the need for engineering effort when
 *  the CMS defines up new tracking.
 */
type CmsEventName = string;

export type EventNamesList = Values<typeof EventNames> | CmsEventName;

export type IdentifyTrait = Partial<{
    user_uuid: string | null;
    user_id: number | null;
    registered: boolean | null;
    signed_in_status: string;
    about_me_status: string | null;
    registration_platform: string | null;
    user_creation_date: string | null;
    consent_1: boolean;
    consent_2: boolean;
    consent_3: boolean;
    consent_4: boolean;
}>;

export type UserTaxonomy = Pick<
    IdentifyTrait,
    | 'about_me_status'
    | 'registered'
    | 'registration_platform'
    | 'signed_in_status'
    | 'user_creation_date'
    | 'user_id'
    | 'user_uuid'
>;

export type { PropertyResult } from './property';
